import { useState, useEffect, useCallback } from 'react';
import { DailyContent, DateInfo } from '@/types';
import { contentService, lunarService } from '@/services';

interface UseDailyContentResult {
  content: DailyContent | null;
  dateInfo: DateInfo;
  isLoading: boolean;
  error: string | null;
  refreshContent: () => Promise<void>;
}

export const useDailyContent = (): UseDailyContentResult => {
  const [content, setContent] = useState<DailyContent | null>(null);
  const [dateInfo, setDateInfo] = useState<DateInfo>(lunarService.getDateInfo());
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadContent = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      const newContent = await contentService.getDailyContent();
      setContent(newContent);
      
      // 触发 Unsplash 下载事件
      if (newContent.image.download_location) {
        await contentService.triggerImageDownload(newContent.image.download_location);
      }
    } catch (err) {
      console.error('加载内容失败:', err);
      setError(err instanceof Error ? err.message : '加载失败，请刷新重试');
    } finally {
      setIsLoading(false);
    }
  }, []);

  const refreshContent = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      const newContent = await contentService.refreshContent();
      setContent(newContent);
      
      // 触发 Unsplash 下载事件
      if (newContent.image.download_location) {
        await contentService.triggerImageDownload(newContent.image.download_location);
      }
    } catch (err) {
      console.error('刷新内容失败:', err);
      setError(err instanceof Error ? err.message : '刷新失败，请重试');
    } finally {
      setIsLoading(false);
    }
  }, []);

  // 初始化加载内容
  useEffect(() => {
    loadContent();
  }, [loadContent]);

  // 每分钟更新日期信息
  useEffect(() => {
    const updateDateInfo = () => {
      setDateInfo(lunarService.getDateInfo());
    };

    // 立即更新一次
    updateDateInfo();

    // 设置定时器每分钟更新
    const interval = setInterval(updateDateInfo, 60000);

    return () => clearInterval(interval);
  }, []);

  return {
    content,
    dateInfo,
    isLoading,
    error,
    refreshContent
  };
};